import Phaser from 'phaser';
import { mapProgress } from '../systems/MapProgress';
import { getSaveProvider } from '../systems/save/saveProviderFactory';
import { WEAPON_UNLOCKS } from '../constants/progression/WeaponUnlocks';
import { ALL_PUDDLE_KEYS } from '../constants/puddles';
import { MUSIC_SOURCES, SFX_SOURCES } from '../constants/audio';

export class BootScene extends Phaser.Scene {
  constructor() {
    super({ key: 'BootScene' });
  }

  preload(): void {
    // Фон стартового экрана (MenuScene) — карта-террейн. 16:9 webp, тянется
    // на весь canvas через cover-fit в MenuScene.relayoutMenu(). Можно
    // переиспользовать в MapScene позже как подложку под ноды уровней.
    this.load.image('map_bg', 'assets/map.webp');

    // Рига игрока: ноги — покадровый спрайтшит (2×2 grid, 4 кадра, лист
    // 2886×3024 → frame 1443×1512), верх — статичная картинка per-weapon
    // (rig_top_<id>), full-frame 1279×935. TOP лицом ВПРАВО → в Player.ts
    // setFlipX(true) на дефолт. Ноги лицом ВЛЕВО (имя файла legs_walk_left)
    // → без флипа, ассеты уже смотрят в одну сторону после всех флипов.
    this.load.spritesheet('player_legs_walk', 'assets/player/legs_walk_left_shee.png', {
      frameWidth: 1443,
      frameHeight: 1512,
    });
    for (const { weaponId } of WEAPON_UNLOCKS) {
      this.load.image(`rig_top_${weaponId}`, `assets/player/rig_top_${weaponId}.png`);
    }

    // SFX — мульти-формат (ogg + m4a фолбэк для iOS/Safari). Источники в
    // constants/audio.ts SFX_SOURCES.
    for (const [key, sources] of Object.entries(SFX_SOURCES)) {
      this.load.audio(key, sources);
    }
    // Музыка — мульти-формат: ogg для Chromium/Firefox/Android, m4a — фолбэк
    // для iOS Safari/Telegram Mini App (там ogg не декодируется). Phaser
    // выбирает первый поддерживаемый. Источники — в constants/audio.ts.
    for (const [key, sources] of Object.entries(MUSIC_SOURCES)) {
      this.load.audio(key, sources);
    }

    // Иконки оружия для слотов магазина/инвентаря. Конвенция: texture key
    // = weapon_<id>, файл — public/assets/weapon/weapon_<id>.png. HUD сам подхватит
    // через this.textures.exists(...) в createWeaponSlotsHUD — рендерит иконку
    // только если текстура загружена.
    //
    // Источник id — WEAPON_UNLOCKS: туда обязательно добавляется любое новое оружие
    // для прогрессии магазина. Добавил оружие в WeaponUnlocks + положил PNG →
    // иконка подхватывается автоматически, BootScene править не нужно.
    for (const { weaponId } of WEAPON_UNLOCKS) {
      this.load.image(`weapon_${weaponId}`, `assets/weapon/weapon_${weaponId}.png`);
    }

    // Спрайт-листы врагов. Конвенция: texture key = enemy_<id>, файл
    // public/assets/enemy/<id>_run_spritesheet.png. Enemy сам подхватит спрайт
    // если соответствующая текстура существует (см. Enemy.ts → SPRITE config).
    // Размеры frameWidth/frameHeight у врагов разные — задаются вручную для каждого.
    // Новый заяц — 4 кадра в 2×2 grid (2494×1552 / 1247×776 каждый). Phaser
    // читает grid left-to-right, top-to-bottom через generateFrameNumbers.
    this.load.spritesheet('enemy_rabbit', 'assets/enemy/rabbit_run_sheet.png', {
      frameWidth: 1247,
      frameHeight: 776,
    });
    // Новая мышь — 4 кадра в 2×2 grid (2742×1118 / 1371×559 каждый). Phaser
    // читает grid left-to-right, top-to-bottom через generateFrameNumbers.
    this.load.spritesheet('enemy_mouse', 'assets/enemy/mouse_run_sheet.png', {
      frameWidth: 1371,
      frameHeight: 559,
    });
    this.load.spritesheet('enemy_boarling', 'assets/enemy/boarling_run_spritesheet.png', {
      frameWidth: 549,
      frameHeight: 494,
    });
    this.load.spritesheet('enemy_fox', 'assets/enemy/fox_run_spritesheet.png', {
      frameWidth: 519,
      frameHeight: 607,
    });
    // Новый жук — 3 кадра горизонтальной полосой (1866×340 / 622×340 каждый).
    this.load.spritesheet('enemy_beetle', 'assets/enemy/beetle_run_sheet.png', {
      frameWidth: 622,
      frameHeight: 340,
    });
    this.load.spritesheet('enemy_deer', 'assets/enemy/deer_run_spritesheet.png', {
      frameWidth: 491,
      frameHeight: 509,
    });
    this.load.spritesheet('enemy_kangaroo', 'assets/enemy/kangaroo_run_spritesheet.png', {
      frameWidth: 376,
      frameHeight: 415,
    });
    this.load.spritesheet(
      'enemy_kangaroo_joey',
      'assets/enemy/kangaroo_joey_run_spritesheet.png',
      { frameWidth: 420, frameHeight: 412 },
    );
    // Новый волк — 4 кадра в 2×2 grid (1148×720 / 574×360 каждый). Phaser
    // читает grid left-to-right, top-to-bottom: 0=ВЛ, 1=ВП, 2=НЛ, 3=НП
    // (контакт → сбор → толчок → растяжка).
    this.load.spritesheet('enemy_wolf', 'assets/enemy/wolf_run_sheet.png', {
      frameWidth: 574,
      frameHeight: 360,
    });
    // Boss extends Enemy → конвенция спрайта та же: texture key = enemy_<id>.
    // Файл лежит в public/assets/enemy/ — отдельная папка boss/ не понадобилась.
    // Новый кабан — 4 кадра в 2×2 grid (1332×720 / 666×360 каждый). Phaser
    // читает grid left-to-right, top-to-bottom через generateFrameNumbers.
    this.load.spritesheet('enemy_big_boar', 'assets/enemy/boar_run_sheet.png', {
      frameWidth: 666,
      frameHeight: 360,
    });
    // Новый лось — 4 кадра в 2×2 grid (1354×840 / 677×420 каждый). Phaser
    // читает grid left-to-right, top-to-bottom через generateFrameNumbers.
    this.load.spritesheet('enemy_elk', 'assets/enemy/elk_run_sheet.png', {
      frameWidth: 677,
      frameHeight: 420,
    });
    // Медоед — run-spritesheet 4 кадра, прозрачный фон (заменил старую версию
    // на чёрном фоне). PNG 2048×171 → frame 512×171 (низкий вытянутый зверь).
    this.load.spritesheet(
      'enemy_honey_badger',
      'assets/enemy/honey_badger_run.png',
      { frameWidth: 512, frameHeight: 171 },
    );
    this.load.spritesheet('enemy_camel', 'assets/enemy/camel_run.png', {
      frameWidth: 512,
      frameHeight: 271,
    });
    // Снаряд верблюда — 3 кадра анимации, крутится в полёте. Texture key
    // 'camel_spit' (без 'enemy_' префикса — это снаряд, не враг).
    this.load.spritesheet('camel_spit', 'assets/enemy/camel_spit.png', {
      frameWidth: 400,
      frameHeight: 145,
    });
    this.load.spritesheet('enemy_bear', 'assets/enemy/bear_run.png', {
      frameWidth: 512,
      frameHeight: 257,
    });
    // Боевые ассеты медведя.
    // Камень — 6-кадровая spritesheet анимация кувыркания в полёте (запекли в
    // спрайт-кадры, движком rotation не крутим). frameWidth/Height из PNG
    // 1536×256: 1536/6 = 256.
    this.load.spritesheet('bear_rock', 'assets/enemy/bear_rock.png', {
      frameWidth: 256,
      frameHeight: 256,
    });
    // Слизь-хазард на земле, instakill при контакте, ~4с lifetime.
    this.load.image('bear_slime', 'assets/enemy/bear_slime.png');
    // Beetle: статичная текстура закрытого панциря для фазы invulnerable.
    // Enemy подхватит через EnemyConfig.shellTextureKey = 'beetle_shell'.
    this.load.image('beetle_shell', 'assets/enemy/beetle_shell.png');

    // Декаль-лужи от смерти врагов. Источник имён — PUDDLES_BY_ENEMY в
    // constants/puddles. Добавил врага → дописал две строки в map → load
    // подхватит без правки BootScene.
    for (const key of ALL_PUDDLE_KEYS) {
      this.load.image(key, `assets/enemy/${key}.png`);
    }

    // Фоны: поляна на игровом поле + боковые полосы (лес слева, поле справа).
    // Кладутся в GameScene через layoutBackgrounds — Image растягивается под
    // фактические границы поля/полос, пропорции не сохраняются (тянем как есть).
    // Поляна растягивается на весь canvas. Боковые полосы (bg_forest /
    // bg_corn) убраны — врагов теперь видно с момента входа в canvas.
    this.load.image('bg_clearing', 'assets/bg/bg_clearing.webp');
  }

  create(): void {
    // Анимации врагов. Создаются один раз в BootScene — anims system Phaser
    // глобальная, переживает scene.restart. Конвенция: key = '<id>_run'.
    // Enemy подхватит автоматом (SPRITE_ANIM_KEY_BY_ID в Enemy.ts).
    if (!this.anims.exists('player_legs_walk')) {
      // 4-кадровый walk-цикл (контакт A → проход A → контакт B → проход B).
      // frameRate 8 = ~2 шага/сек на базе. Player.ts крутит timeScale по
      // фактической скорости (0.3..1.5 от MOVE_SPEED) — ноги идут синхронно.
      this.anims.create({
        key: 'player_legs_walk',
        frames: this.anims.generateFrameNumbers('player_legs_walk', { start: 0, end: 3 }),
        frameRate: 8,
        repeat: -1,
      });
    }
    if (!this.anims.exists('rabbit_run')) {
      this.anims.create({
        key: 'rabbit_run',
        frames: this.anims.generateFrameNumbers('enemy_rabbit', { start: 0, end: 3 }),
        frameRate: 8,
        repeat: -1,
      });
    }
    if (!this.anims.exists('mouse_run')) {
      this.anims.create({
        key: 'mouse_run',
        frames: this.anims.generateFrameNumbers('enemy_mouse', { start: 0, end: 3 }),
        frameRate: 10,
        repeat: -1,
      });
    }
    if (!this.anims.exists('boarling_run')) {
      this.anims.create({
        key: 'boarling_run',
        frames: this.anims.generateFrameNumbers('enemy_boarling', { start: 0, end: 3 }),
        frameRate: 8,
        repeat: -1,
      });
    }
    if (!this.anims.exists('fox_run')) {
      this.anims.create({
        key: 'fox_run',
        frames: this.anims.generateFrameNumbers('enemy_fox', { start: 0, end: 3 }),
        frameRate: 8,
        repeat: -1,
      });
    }
    if (!this.anims.exists('beetle_run')) {
      this.anims.create({
        key: 'beetle_run',
        frames: this.anims.generateFrameNumbers('enemy_beetle', { start: 0, end: 2 }),
        frameRate: 10,
        repeat: -1,
      });
    }
    if (!this.anims.exists('deer_run')) {
      this.anims.create({
        key: 'deer_run',
        frames: this.anims.generateFrameNumbers('enemy_deer', { start: 0, end: 3 }),
        frameRate: 8,
        repeat: -1,
      });
    }
    if (!this.anims.exists('kangaroo_run')) {
      this.anims.create({
        key: 'kangaroo_run',
        frames: this.anims.generateFrameNumbers('enemy_kangaroo', { start: 0, end: 3 }),
        frameRate: 8,
        repeat: -1,
      });
    }
    if (!this.anims.exists('kangaroo_joey_run')) {
      this.anims.create({
        key: 'kangaroo_joey_run',
        frames: this.anims.generateFrameNumbers('enemy_kangaroo_joey', { start: 0, end: 3 }),
        frameRate: 11,
        repeat: -1,
      });
    }
    if (!this.anims.exists('wolf_run')) {
      this.anims.create({
        key: 'wolf_run',
        frames: this.anims.generateFrameNumbers('enemy_wolf', { start: 0, end: 3 }),
        frameRate: 8,
        repeat: -1,
      });
    }
    if (!this.anims.exists('big_boar_run')) {
      this.anims.create({
        key: 'big_boar_run',
        frames: this.anims.generateFrameNumbers('enemy_big_boar', { start: 0, end: 3 }),
        frameRate: 8,
        repeat: -1,
      });
    }
    if (!this.anims.exists('elk_run')) {
      this.anims.create({
        key: 'elk_run',
        frames: this.anims.generateFrameNumbers('enemy_elk', { start: 0, end: 3 }),
        frameRate: 10,
        repeat: -1,
      });
    }
    if (!this.anims.exists('honey_badger_run')) {
      this.anims.create({
        key: 'honey_badger_run',
        frames: this.anims.generateFrameNumbers('enemy_honey_badger', { start: 0, end: 3 }),
        frameRate: 12,
        repeat: -1,
      });
    }
    if (!this.anims.exists('camel_run')) {
      this.anims.create({
        key: 'camel_run',
        frames: this.anims.generateFrameNumbers('enemy_camel', { start: 0, end: 3 }),
        frameRate: 11,
        repeat: -1,
      });
    }
    if (!this.anims.exists('camel_spit')) {
      this.anims.create({
        key: 'camel_spit',
        frames: this.anims.generateFrameNumbers('camel_spit', { start: 0, end: 2 }),
        frameRate: 12,
        repeat: -1,
      });
    }
    if (!this.anims.exists('bear_run')) {
      this.anims.create({
        key: 'bear_run',
        frames: this.anims.generateFrameNumbers('enemy_bear', { start: 0, end: 2 }),
        frameRate: 9,
        repeat: -1,
      });
    }
    if (!this.anims.exists('bear_rock_spin')) {
      this.anims.create({
        key: 'bear_rock_spin',
        frames: this.anims.generateFrameNumbers('bear_rock', { start: 0, end: 5 }),
        frameRate: 14,
        repeat: -1,
      });
    }

    const loadingText = this.add
      .text(this.scale.width / 2, this.scale.height / 2, 'Загрузка...', {
        fontFamily: 'Arial',
        fontSize: '28px',
        color: '#e8e8e8',
      })
      .setOrigin(0.5);

    // Resize только пересчитывает позицию текста — async-загрузка прогресса
    // не должна перезапускаться, чтобы не дёргать CloudStorage.
    const onResize = (): void => {
      loadingText.setPosition(this.scale.width / 2, this.scale.height / 2);
    };
    this.scale.on('resize', onResize, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.scale.off('resize', onResize, this);
    });

    // CloudStorage может тормозить — асинхронно ждём, потом переходим в меню.
    // Ошибки уже логируются внутри loadFromProvider, до меню всё равно доходим.
    this.loadProgressAndStart(loadingText);
  }

  private async loadProgressAndStart(loadingText: Phaser.GameObjects.Text): Promise<void> {
    const provider = getSaveProvider();
    await mapProgress.loadFromProvider(provider);
    loadingText.destroy();
    // MenuScene снесён из бутстрап-флоу — на мобилке его «Нажмите чтобы начать»
    // + «ИГРАТЬ» = два лишних тапа до боя. Сразу карта с CTA «В БОЙ»; первый
    // тап по сцене сам разблокирует audio context (в MapScene first-tap hook).
    this.scene.start('MapScene');
  }
}
